import { useState } from "react";
import type { OfferProfile, QualificationCriterion } from "@shared/lib/offer-profiles-shared";
import { useT } from "@shared/client/locale";
import { cn } from "@shared/lib/utils";

/**
 * Le ciblage d'une offre en lecture. L'agent écrit des paragraphes : on les
 * replie à quelques lignes, et on met en liste ce qui est une énumération
 * (déclencheurs, exclusions). Le formulaire n'apparaît que sur « Modifier ».
 */

const CLAMP = { 2: "line-clamp-2", 3: "line-clamp-3" } as const;

/** Texte long replié, déplié à la demande. */
function Clamp({ text, lines = 3 }: { text: string; lines?: keyof typeof CLAMP }) {
  const t = useT().settings.profileView;
  const [open, setOpen] = useState(false);
  const long = text.length > lines * 110;
  return (
    <div className="grid justify-items-start gap-1">
      <p className={cn("m-0 text-[14px] leading-[1.6] whitespace-pre-line", long && !open && CLAMP[lines])}>{text}</p>
      {long && (
        <button
          className="cursor-pointer border-0 bg-transparent p-0 text-[12.5px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
          onClick={() => setOpen(!open)}
          type="button"
        >
          {open ? t.showLess : t.readMore}
        </button>
      )}
    </div>
  );
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/** Une énumération : une ligne par entrée, ou à défaut les virgules hors parenthèses. */
function entries(text: string, splitCommas: boolean) {
  const separator = splitCommas ? /\n+|[;,](?![^(]*\))/ : /\n+/;
  return text
    .split(separator)
    .map((entry) => entry.trim().replace(/^[-•]\s*/, "").replace(/\.$/, ""))
    .filter(Boolean)
    .map(capitalize);
}

function List({ text, splitCommas = false }: { text: string; splitCommas?: boolean }) {
  const items = entries(text, splitCommas);
  if (items.length < 2) return <Clamp lines={2} text={text} />;
  return (
    <ul className="m-0 grid list-disc gap-1 pl-4 text-[14px] leading-[1.5] marker:text-muted-foreground">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5 border-t py-4 first:border-t-0 md:grid-cols-[150px_1fr] md:gap-6">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </div>
  );
}

/** Ce que l'enquête cherche sur chaque lead : le critère, puis ce qui est un bon signe. */
function Criteria({ criteria }: { criteria: QualificationCriterion[] }) {
  const t = useT().settings.profileView;
  return (
    <ul className="m-0 grid list-none gap-2 p-0 text-[14px] leading-[1.5]">
      {criteria.map((criterion) => (
        <li key={criterion.label}>
          <span className="font-medium">{criterion.label}</span>
          {criterion.decisive && <span className="text-muted-foreground"> · {t.decisive}</span>}
          {criterion.good_sign && <span className="block text-[13px] text-muted-foreground">{criterion.good_sign}</span>}
        </li>
      ))}
    </ul>
  );
}

export function hasTargeting(profile: OfferProfile) {
  return Boolean(
    profile.offer_summary || profile.priority_segment || profile.decision_maker || profile.favorable_event || profile.exclusions ||
      profile.user_notes || (profile.qualification_criteria ?? []).length > 0,
  );
}

export function OfferProfileView({ profile }: { profile: OfferProfile }) {
  const labels = useT().settings.profileView.rows;
  const rows: [string, React.ReactNode][] = [
    [labels.summary, profile.offer_summary && <Clamp text={profile.offer_summary} />],
    [labels.segment, profile.priority_segment && <Clamp lines={2} text={profile.priority_segment} />],
    [labels.decisionMaker, profile.decision_maker && <p className="m-0 text-[14px]">{profile.decision_maker}</p>],
    [labels.triggers, profile.favorable_event && <List splitCommas text={profile.favorable_event} />],
    [labels.exclusions, profile.exclusions && <List text={profile.exclusions} />],
    [labels.criteria, (profile.qualification_criteria ?? []).length > 0 && <Criteria criteria={profile.qualification_criteria} />],
    [labels.notes, profile.user_notes && <Clamp lines={2} text={profile.user_notes} />],
  ];
  return (
    <dl className="m-0">
      {rows
        .filter(([, value]) => value)
        .map(([label, value]) => (
          <Row key={label} label={label}>
            {value}
          </Row>
        ))}
    </dl>
  );
}
